import { Box, Button, Card, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormProvider, RHFTextField, RHFUploadAvatar } from 'src/components/hook-form';
import TimeZoneSelect from 'src/components/time-zone/time-zone.component';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import ValidationTool from 'src/utils/validationHelper';

const BrandDialog = ({ handleSkip, showToast, successCallback, failureCallback, onBoardingInfo }) => {
  const { t: translate } = useTranslation();
  const maxFileSizeInBytes = 10485760;
  const supportedFileFormat = ['image/jpeg', 'image/png', 'image/jpg'];

  // State Variables
  const [brandName, setBrandName] = useState('');
  const [brandImage, setBrandImage] = useState('');
  const [brandDescription, setBrandDescription] = useState('');
  const [timeZone, setTimeZone] = useState('UTC');
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [fileUploadProgress, setFileUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);

  const defaultValues = {
    brandName: brandName || '',
    brandImage: brandImage || '',
    brandDescription: brandDescription || '',
    timeZone: timeZone || '',
  };

  useEffect(() => {
    generateFormModelSchema();
    if (onBoardingInfo?.org_done || onBoardingInfo?.org_skipped) {
      setOpenBrandDialog(true);
    }
  }, [onBoardingInfo]);

  const generateFormModelSchema = () => {
    let fields = [ValidationTool.GenerateSchema('brandName', translate('brand_dialog.brand_name_label'), 'TEXT', true)];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'brandName':
        setBrandName(value);
        break;
      case 'brandDescription':
        setBrandDescription(value);
        break;
      case 'timezone':
        setTimeZone(value);
        break;
      default:
        break;
    }
  };

  const handleDeleteBrandImage = () => {
    setBrandImage('');
  };

  const handleDrop = (acceptedFiles, iconType) => {
    const file = acceptedFiles[0];
    handleFileUpload(file, iconType);
  };

  const handleFileUpload = (file, iconType) => {
    let isFileInCorrectFormat = supportedFileFormat.some((a) => a.includes(file.type));
    if (isFileInCorrectFormat) {
      if (file.size < maxFileSizeInBytes) {
        setFileUploadProgress(1);
        let formData = new FormData();
        formData.append('fileupload', file);
        axios
          .post(imageModeuleUrl.uploadMedia, formData, {
            headers: HttpServices.GetRequestHeader('form', true),
            onUploadProgress: (data) => {
              let loadedProgress = Math.round((100 * data.loaded) / data.total);
              setFileUploadProgress(loadedProgress > 90 ? 90 : loadedProgress);
            },
          })
          .then((response) => {
            setFileUploadProgress(100);
            if (response.status === 200 && response.data.http_code === 200)
              fileUploadSuccessCallback(
                response.data.data,
                iconType,
                translate('manual_onboarding.file_uploaded_success', 'File Uploaded Successfully')
              );
            else
              showToast(
                translate(
                  'manual_onboarding.something_went_wrong',
                  'Something went wrong. Please contact our technical team'
                ),
                'e'
              );
            setFileUploadProgress(0);
          })
          .catch((error) => {
            setFileUploadProgress(0);
            showToast(error.toString(), 'e');
          });
      } else
        showToast(
          translate(
            'manual_onboarding.max_file_size',
            'Maximum file size for image is 10MB. Please select image below 10 MB'
          ),
          'e'
        );
    } else {
      showToast(
        translate(
          'manual_onboarding.unsupported_format',
          'We only support JPEG, and PNG file formats. The selected file are not in the supported format'
        ),
        'e'
      );
    }
  };

  const fileUploadSuccessCallback = (data, iconType, message) => {
    if (iconType === 'brandImage') setBrandImage(data.media_data[0].media_url);
    showToast(message, 's');
  };

  const methods = useForm({
    defaultValues,
  });

  const { handleSubmit } = methods;

  const onSubmit = () => {
    const formattedTimeZone = timeZone.split(':')[0].trim();
    let brandForm = {
      brand_name: brandName,
      brand_image: brandImage,
      description: brandDescription,
      timezone: formattedTimeZone,
    };
    let postProps = {
      url: userModuleURL.updateBrand,
      body: brandForm,
      successCallback: (data, message) => successCallback(data, message, 'brand'),
      failureCallback: brandInfoFailureCallback,
    };
    setIsSubmitting(true);
    HttpServices.Post(postProps).finally(() => {
      setIsSubmitting(false);
      setOpenBrandDialog(false);
    });
  };
  const brandInfoFailureCallback = (message) => {
    setIsSubmitting(false);
    failureCallback(message);
  };

  const handleSubmitForm = () => {
    let userForm = {
      brandName: brandName,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, userForm);
    if (isValidForm) {
      setFormValidationErrors({});
      onSubmit();
    } else {
      setFormValidationErrors(formValidationErrors);
    }
  };

  return (
    <Dialog
      open={openBrandDialog && !onBoardingInfo?.brand_done && !onBoardingInfo?.brand_skipped}
      onClose={() => setOpenBrandDialog(false)}
      maxWidth="md"
      fullWidth
    >
      {/* Top-right Skip Button */}
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <Button variant="contained" color="primary" onClick={handleSkip}>
          {translate('brand_dialog.skip_button')}
        </Button>
      </Box>
      <DialogTitle>{translate('brand_dialog.title')}</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
        <DialogContent>
          <Card
            sx={{
              pt: 2,
              pb: 2,
              px: 2,
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <RHFUploadAvatar
              name="brandImage"
              accept="image/*"
              maxSize={5242880}
              onDrop={(e) => {
                handleDrop(e, 'brandImage');
              }}
              handleDeleteProfiles={handleDeleteBrandImage}
              url={brandImage}
              helperText={
                <Typography
                  variant="caption"
                  sx={{
                    mt: 2,
                    mx: 'auto',
                    display: 'block',
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                  dangerouslySetInnerHTML={{ __html: translate('brand_dialog.avatar_helper_text') }}
                />
              }
            />
          </Card>
          <Card sx={{ p: 1, mt: 3 }}>
            <Box
              sx={{
                display: 'grid',
                rowGap: 3,
                columnGap: 2,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                name="brandName"
                label={translate('brand_dialog.brand_name_label')}
                value={brandName}
                onChange={handleInputChange}
                error={formValidationErrors?.brandName}
                helperText={formValidationErrors?.brandName}
              />
              <TimeZoneSelect name="timezone" value={timeZone} onChange={handleInputChange} />
              <RHFTextField
                name="brandDescription"
                label={translate('brand_dialog.brand_description_label')}
                multiline
                rows={5}
                value={brandDescription}
                onChange={handleInputChange}
              />
            </Box>
          </Card>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" type="submit" disabled={isSubmitting} sx={{ minWidth: 120 }}>
              {translate('brand_dialog.submit_button')}
            </Button>
          </Box>
        </DialogContent>
      </FormProvider>
      {/* Remove DialogActions since Skip Button is moved */}
    </Dialog>
  );
};

export default BrandDialog;
